import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Search, 
  Filter, 
  TrendingUp,
  Users,
  AlertTriangle
} from 'lucide-react'
import { useDeals } from '@/hooks/useDeals'
import { HealthCard, HealthCardSkeleton, EvidenceIngestionModal, CreateDealModal } from '@/components'
import UserMenu from '@/components/UserMenu'
import { clsx } from 'clsx'

export default function DealDashboard() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDealForEvidence, setSelectedDealForEvidence] = useState<{
    id: string
    name: string
  } | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  
  const { data: dealsData, isLoading, error } = useDeals()

  // 模拟体检卡数据（实际应从各商机的health-card API获取）
  const mockHealthCards = dealsData?.items?.map((deal: any) => ({
    dealId: deal.id,
    dealName: deal.name,
    score: deal.score || 0,
    confidence: deal.confidence || 0,
    cardState: {
      stateType: deal.score > 70 ? 'green' : deal.score > 40 ? 'yellow' : deal.evidenceCount === 0 ? 'cold' : 'red',
      color: deal.score > 70 ? 'green' : deal.score > 40 ? 'amber' : 'red',
      icon: 'fa-check',
      title: deal.evidenceCount === 0 ? '开启项目诊断' : deal.fatalGapCount > 0 ? `检测到 ${deal.fatalGapCount} 个致命缺口` : '项目健康运行',
      description: deal.evidenceCount === 0 ? '请添加第一条证据以启动诊断' : `已录入 ${deal.evidenceCount} 条证据`,
      quote: '',
      actionText: deal.evidenceCount === 0 ? '添加证据' : '查看详情',
      score: String(deal.score || '--'),
      scoreColor: deal.score > 70 ? 'text-green-600' : deal.score > 40 ? 'text-amber-600' : 'text-red-600',
      tag: deal.evidenceCount === 0 ? '待启动' : deal.fatalGapCount > 0 ? '需关注' : '正常',
      tagColor: deal.evidenceCount === 0 ? 'bg-gray-100 text-gray-500' : deal.fatalGapCount > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600',
    },
    fatalGaps: [],
    nextActions: [],
    evidenceCount: deal.evidenceCount || 0,
    lastEvidenceDate: null,
    daysWithoutStrongSignal: undefined,
    displayMode: deal.evidenceCount === 0 ? 'cold_start' : 'ideal',
  })) || []

  // 过滤商机
  const filteredDeals = mockHealthCards.filter((card: any) =>
    card.dealName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // 统计数据
  const stats = {
    total: mockHealthCards.length,
    atRisk: mockHealthCards.filter((c: any) => c.cardState.stateType === 'red').length,
    healthy: mockHealthCards.filter((c: any) => c.cardState.stateType === 'green').length,
    avgScore: mockHealthCards.length > 0
      ? Math.round(mockHealthCards.reduce((sum: number, c: any) => sum + (c.score || 0), 0) / mockHealthCards.length)
      : 0,
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* 顶部导航 */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">
                销售机会看板
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                证据驱动的赢单率引擎
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
              >
                <Plus className="w-4 h-4" />
                新建机会
              </button>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* 统计卡片 */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: '总机会数', value: stats.total, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: '健康运行', value: stats.healthy, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
            { label: '需关注', value: stats.atRisk, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: '平均评分', value: stats.avgScore, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4"
            >
              <div className={clsx('p-3 rounded-xl', stat.bg)}>
                <stat.icon className={clsx('w-5 h-5', stat.color)} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 搜索和过滤 */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索机会名称..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            筛选
          </button>
        </div>

        {/* 机会列表 */}
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <HealthCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-gray-600">加载失败，请刷新重试</p>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">暂无销售机会</p>
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
            >
              创建第一个机会
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDeals.map((card: any, index: number) => (
              <motion.div
                key={card.dealId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/deal/${card.dealId}`}>
                  <HealthCard
                    data={card}
                    compact
                    onClick={() => {
                      if (card.cardState.stateType === 'cold') {
                        setSelectedDealForEvidence({ id: card.dealId, name: card.dealName })
                      }
                    }}
                  />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* 创建机会弹窗 */}
      <CreateDealModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {/* 证据录入弹窗 */}
      <EvidenceIngestionModal
        isOpen={!!selectedDealForEvidence}
        onClose={() => setSelectedDealForEvidence(null)}
        dealId={selectedDealForEvidence?.id || ''}
        dealName={selectedDealForEvidence?.name || ''}
      />
    </div>
  )
}
